import { useEffect, useMemo, useRef, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import ApplicationsFilter, { ApplicationFilterState, } from "../../components/ApplicationsFilter/ApplicationsFilter";
import { Dialog } from "../../components/CommonComponents/Dialog/Dialog";
import Paginator, { PaginatorForRDT, } from "../../components/CommonComponents/Paginator/Paginator";
import SortIcon from "../../components/CommonComponents/SortIcon/SortIcon";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { getApplicationTypeColumnLabel } from "../../config/phaseConstants";
import { TApplication } from "../../hooks/applications.hooks";
import { useDebounce, useToggle } from "../../hooks/common";
import { formatDate } from "../../utils/date";
import ApplicationListViewToggle from "../../components/ApplicationListViewToggle/ApplicationListViewToggle";
import {
  ApplicationListView,
  DEFAULT_APPLICATION_LIST_VIEW,
} from "../../config/pendingApplicationView";
import { useLevelThreeApplicationCounts, useLevelThreeApplications, } from "../../hooks/levelThreeApplication.hooks";
import { _axios } from "../../utils/axios";
import { BASE_URL } from "../../constants/Constants";
import { ErrorToast } from "../../components/toastMessage";
import { useUser } from "../../contexts/UserContext";
import style from "../../components/pageHeader/pageHeader.module.css";
import BoxComponent from "../../components/CommonComponents/BoxComponent/BoxComponent";
import { OverrideButton, ViewButton, } from "../../components/CommonComponents/EditComponent/EditComponent";
import OverrideDialog from "../../components/CommonComponents/DialogConfirmation/DialogModal";

const statusesApplicableForPaymentScreen = [
    "Pending Payment Level 3 Review",
    "Pending Final Review",
    "Pending Level 2 Final Review",
    "Pending Payment & Application Rejection Confirmation",
];

/**
 * Statuses Level 3 can override, and the payment reviews among them (an
 * override there opens the Level 3 payment page).
 *
 * BEFORE - Level 1's payment review was missing from both rules; it was
 * added to the payment flow after the Override rule was written:
 *
 * e.status === "Pending Level 1 Review" ||
 * e.status === "Pending Level 2 Review" ||
 * e.status === "Pending Level 2 Final Review"
 *
 * return status === "Pending Level 2 Final Review" ? "payment" : "application";
 */
const paymentOverrideStatuses = ["Pending Level 1 Final Review", "Pending Level 2 Final Review"];
const overridableStatuses = ["Pending Level 1 Review", "Pending Level 2 Review", ...paymentOverrideStatuses];

const levelThreeActionable = [
    "Pending Level 3 Review",
    "Pending Payment Level 3 Review",
    "Pending Final Review",
    "Payment Rejected By Level 3 Reviewer",
    "Returned For Repayment By Level 3 Reviewer",
    "Rejected By Level 3 Reviewer",
    "Returned By Level 3 Reviewer",
    "Pending Rejection Confirmation",
];

const LevelThreeApplications = () => {
    const [overrideReason, setOverrideReason] = useState("");
    const user = useUser();
    const navigate = useNavigate();
    const [filterOpen, toggleFilter] = useToggle();
    const [filterState, setFilterState] = useState<ApplicationFilterState>({
        connector: "and",
        conditions: [],
    });
    const [searchQuery, setSearchQuery] = useState("");
    const [view, setView] = useState<"table" | "card">("table");
    const [page, setPage] = useState(1);
    /**
     * The list opens on the applications waiting for Level 3 (System Admin
     * with Admin Mode off lands here too); "Show All Applications" switches to
     * the full list. Changing it goes back to page 1, since the other view may
     * have fewer pages.
     */
    const [listView, setListView] = useState<ApplicationListView>(DEFAULT_APPLICATION_LIST_VIEW);
    const changeListView = (next: ApplicationListView) => {
        setListView(next);
        setPage(1);
    };
    const [isOverRide, setIsOverRide] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedApplication, setSelectedApplication] =
        useState<TApplication | null>(null);
    const search = useDebounce(searchQuery);
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
    /*const [{ sortKey, sortOrder }, setSort] = useState({
        // sortKey: "applicationId",
        // sortOrder: 1,
        sortKey: "submittedAt",
        sortOrder: -1,
    });*/

    const [{ sortKey, sortOrder }, setSort] = useState<{
        sortKey: string | null;
        sortOrder: 1 | -1;
    }>({
        sortKey: null,
        sortOrder: -1,
    });

    const { isLoading: isLoading1, data: counts } =
        useLevelThreeApplicationCounts({
            search,
            filters: filterState,
            view: listView,
        });
    const { isLoading: isLoading2, data } = useLevelThreeApplications({
        page,
        search,
        limit: 10,
        sort: sortKey ?? "",
        sortVal: sortOrder,
        filters: filterState,
        view: listView,
    });
    console.log("🧪 raw data:", data);
    const getOverRideControl = () => {
        _axios
            .post(`${BASE_URL}/api/controller/toggle`)
            .then((response) => {
                if (response?.data?.success === 0) {
                    ErrorToast(response?.data?.message);
                    return;
                }
                const data = response.data.data[0];
                setIsOverRide(data.override_control == 1 ? true : false);
                console.log(response);
            })
            .catch((error) => {
                if (error?.response?.data?.message) {
                    ErrorToast(error?.response?.data?.message);
                }
                console.log("error", error);
            });
    };

    /*
    const sortedData = useMemo(() => {
        if (!data?.data) return [];

        const actionable = data.data.filter(a => a.isActionAllowed);
        const nonActionable = data.data.filter(a => !a.isActionAllowed);

        return [...actionable, ...nonActionable];
    }, [data]);
    */

    const sortedData = useMemo(() => data?.data || [], [data]);

    // useEffect(() => {
    //     if (data?.data) {
    //        // console.log("🔥 RAW Level-3 API DATA ↓↓↓");
    //         // console.table(
    //         //     data.data.map((a) => ({
    //         //         id: a.id,
    //         //         applicationId: a.applicationId,
    //         //         status: a.status,
    //         //         submittedAt: a.submittedAt,
    //         //         daysLeftForAction: a.daysLeftForAction,
    //         //         isActionAllowed: a.isActionAllowed,
    //         //     }))
    //         // );
    //     }
    // }, [data]);


    useEffect(() => {
        getOverRideControl();
    }, []);

    const determineOverrideType = (status: string): "application" | "payment" => {
        return paymentOverrideStatuses.includes(status) ? "payment" : "application";
    };

    const handleOverrideClick = (application: TApplication) => {
        setSelectedApplication(application);
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
    };

    const handleOverrideConfirm = () => {
        if (selectedApplication) {
            const currentDate = formatDate(new Date());

            _axios
                .post(`${BASE_URL}/api/override-application/create`, {
                    application_id: selectedApplication.applicationId,
                    override_by: user?.name,
                    user_id: user?.user_id,
                    reason: textAreaRef?.current?.value ?? "",
                    status: selectedApplication.status,
                })
                .then((response) => {
                    if (response?.data?.success === 0) {
                        ErrorToast(response?.data?.message);
                        return;
                    }
                    const isPayment = determineOverrideType(selectedApplication.status) === "payment";
                    navigate(isPayment ? `/level-3-payment-details` : `/level-three-application-detail`, {
                        state: {
                            id: selectedApplication.id,
                            override: true,
                            overrideReason: textAreaRef?.current?.value ?? "",
                            overrideType: isPayment ? "payment" : "application",
                        },
                    });
                    console.log("override-application", response);
                })
                .catch((error) => {
                    if (error?.response?.data?.message) {
                        ErrorToast(error?.response?.data?.message);
                    }
                    console.log("error", error);
                });
        }
        setShowModal(false);
    };

    /**
     * Every row can be opened. A row L3 can act on goes to its action page (the
     * payment page for payment-screen statuses, otherwise the L3 review page for
     * L3-actionable statuses); every other row opens the read-only view page.
     * The Override button keeps its own status rule, since it is a real action.
     */
    const openApplication = (e: TApplication) => {
        if (!e.isActionAllowed) {
            navigate(`/view-application-details`, { state: { id: e.id } });
            return;
        }

        if (statusesApplicableForPaymentScreen.includes(e.status)) {
            navigate(`/level-3-payment-details`, {
                state: { id: e.id },
            });
        } else if (levelThreeActionable.includes(e.status)) {
            navigate(`/level-three-application-detail`, {
                state: { id: e.id },
            });
        } else {
            navigate(`/view-application-details`, {
                state: { id: e.id },
            });
        }
    };

    const columns = useMemo(
        (): TableColumn<TApplication & { serviceName?: string }>[] => [
            {
                name: "Application ID",
                sortable: true,
                width: "190px",
                sortField: "applicationId",
                selector: (d) => d.applicationId,
                center: true,
                grow: 0.5,
                cell: (e) => {
                    const disabled = !e.isActionAllowed;

                    /**
                     * BEFORE: non-actionable rows were greyed out with
                     * cursor-not-allowed and returned early on click:
                     *
                     * className={`font-semibold ${disabled
                     *     ? "text-gray-400 cursor-not-allowed"
                     *     : "text-[#E9B925] cursor-pointer underline"
                     *     }`}
                     * onClick={() => {
                     *     if (disabled) return;
                     *     if (statusesApplicableForPaymentScreen.includes(e.status)) {
                     *         navigate(`/level-3-payment-details`, { state: { id: e.id } });
                     *     } else if (levelThreeActionable.includes(e.status)) {
                     *         navigate(`/level-three-application-detail`, { state: { id: e.id } });
                     *     } else {
                     *         navigate(`/view-application-details`, { state: { id: e.id } });
                     *     }
                     * }}
                     */
                    return (
                        <span
                            className={`font-semibold cursor-pointer underline ${disabled
                                ? "text-gray-400"
                                : "text-[#E9B925]"
                                }`}
                            onClick={() => openApplication(e)}
                        >
                            {e.applicationId}
                        </span>
                    );
                }
            },
            {
                name: "Plot Owner's Name",
                sortable: true,
                sortField: "plot_owner_s_name",
                selector: (d) => d.plot_owner_s_name || "-",
                cell: (d) => (
                    <span className="text-center">{d.plot_owner_s_name || "-"}</span>
                ),
                center: true,
                grow: 2,
            },
            {
                name: "Application Type",
                selector: (d) => getApplicationTypeColumnLabel(d),
                center: true,
                width: "180px",
            },
            {
                name: "Status",
                width: "250px",
                center: true,
                sortable: true,
                sortField: "status",
                cell: (d) => <span className="text-center">{d.status || "-"}</span>,
            },
            {
                name: "Name of Service",
                width: "210px",
                sortable: true,
                sortField: "name",
                selector: (d) => d.serviceName || "-",
                cell: (d) => (
                    <span className="text-center">{d.serviceName || "-"}</span>
                ),
                center: true,
            },
            {
                name: "Days Left for Action",
                sortable: true,
                sortField: "daysLeftForAction",
                width: "150px",
                cell: (d) => {
                    // Add debug logging to see what's received
                   // console.log(`L3 row data for ${d.applicationId}:`, d);

                    // If no value, show "-"
                    if (
                        d.daysLeftForAction === null ||
                        d.daysLeftForAction === undefined
                    ) {
                        return <span className="text-center">-</span>;
                    }

                    // If days left is 0, display with urgent styling
                    if (d.daysLeftForAction === 0) {
                        return (
                            <span className="text-center text-red-600 font-bold">0</span>
                        );
                    }

                    // Normal case with days remaining
                    const isDanger = d.daysLeftForAction <= 2;
                    return (
                        <span
                            className={`text-center ${isDanger ? "text-red-600 font-bold" : ""
                                }`}
                        >
                            {d.daysLeftForAction}
                        </span>
                    );
                },
                center: true,
            },
            {
                name: "Date of Submission",
                center: true,
                sortable: true,
                width: "240px",
                sortField: "submittedAt",
                cell: (d) => (d.submittedAt ? formatDate(d.submittedAt) : "-"),
            },
            {
                name: "Created By",
                selector: (row: any) => row.createdBy,
                cell: (row: any) => (
                    <span className="text-center">{row.createdBy}</span>
                ),
                center: true,
                sortable: true,
                sortField: "createdBy",
                width: "170px",
            },
            {
                name: "Created On",
                selector: (row: any) => formatDate(new Date(row.createdAt)),
                center: true,
                sortable: true,
                sortField: "createdAt",
                width: "170px",
            },
            {
                name: "Modified By",
                selector: (row: any) => row.modifiedBy,
                cell: (row: any) => (
                    <span className="text-center">{row.modifiedBy}</span>
                ),
                center: true,
                sortable: true,
                sortField: "modifiedBy",
                width: "170px",
            },
            {
                name: "Modified On",
                selector: (row: any) => formatDate(new Date(row.updatedAt)),
                center: true,
                sortable: true,
                sortField: "updatedAt",
                width: "170px",
            },
            {
                name: "Action",
                center: true,
                width: "140px",
                grow: 0.5,
                cell: (e) => {
                    /** No longer read: the view button is always clickable. */
                    // const disabled = !e.isActionAllowed;

                    return (
                        <div className="flex space-x-2 justify-center">
                            {/**
                              * BEFORE: disabled for non-actionable rows. Now always
                              * clickable.
                              *
                              * <ViewButton
                              *     disabled={disabled}
                              *     onClick={() => {
                              *         if (disabled) return;
                              *         if (statusesApplicableForPaymentScreen.includes(e.status)) {
                              *             navigate(`/level-3-payment-details`, {
                              *                 state: { id: e.id },
                              *             });
                              *         } else if (levelThreeActionable.includes(e.status)) {
                              *             navigate(`/level-three-application-detail`, {
                              *                 state: { id: e.id },
                              *             });
                              *         } else {
                              *             navigate(`/view-application-details`, {
                              *                 state: { id: e.id },
                              *             });
                              *         }
                              *     }}
                              * />
                              */}
                            <ViewButton onClick={() => openApplication(e)} />

                            <OverrideButton
                                disabled={!overridableStatuses.includes(e.status)}
                                onClick={() => handleOverrideClick(e)}
                            />
                        </div>
                    );
                },
            },
        ],
        // [navigate, isOverRide, showModal, sortKey, sortOrder]
        [navigate, openApplication, isOverRide, showModal, sortKey, sortOrder]
    );

    console.log("📦 sort state:", sortKey, sortOrder, "| data count:", data?.data?.length);
    return (
        <div className="p-4 md:p-8">
            <div className="flex justify-between mb-4 flex-wrap md:flex-nowrap gap-4 items-center">
                {/* <h4 className="text-2xl text-gray-600 font-bold">All Applications</h4> */}
                <h4 className="text-2xl text-gray-600 font-bold">
                    {listView === "pending" ? "Pending Applications" : "All Applications"}
                </h4>
                <div className="flex space-x-4 items-center grow md:grow-0">
                    <ApplicationListViewToggle view={listView} onChange={changeListView} />
                    <button
                        className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center"
                        onClick={() => {
                            setView((e) => (e === "card" ? "table" : "card"));
                            setPage(1);
                        }}
                    >
                        {view === "card" ? (
                            <i className="fa fa-th-large text-primary m-0" />
                        ) : (
                            <i className="fa fa-list-ul text-primary m-0" />
                        )}
                    </button>
                    <button
                        className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center min-w-[42px]"
                        onClick={toggleFilter}
                    >
                        <i className="fa fa-filter text-primary m-0" />
                    </button>
                    <div className={style.searchContainer}>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            className={style.inputSearch}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <button className={`fa fa-search ${style.searchIcon}`} />
                    </div>
                </div>
            </div>
            {/* DataTable or Card view */}
            {view === "card" ? (
                <>
                    <BoxComponent>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {sortedData.map((row: any, index: number) => {
                                return (
                                    <div
                                        className="p-4 rounded-2xl bg-white shadow-lg flex flex-col border border-gray-300"
                                        key={row.id}
                                    >
                                        {/* <div className="flex flex-col h-full">
                                            <div className="flex-grow"> */}
                                        <div className="flex justify-between items-center space-x-4 mb-4">
                                            <div
                                                className="text-center bg-yellow-500 text-grey-600 inline-block py-1 px-4 sm:px-2 rounded-full text-sm text-[12px] font-semibold">
                                                {row.status}
                                            </div>
                                            <div
                                                className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-2">
                                                {/**
                                                  * BEFORE: disabled for non-actionable rows.
                                                  * Now always clickable.
                                                  *
                                                  * <ViewButton
                                                  *     disabled={!row.isActionAllowed}
                                                  *     onClick={() => {
                                                  *         if (!row.isActionAllowed) return;
                                                  *         if (statusesApplicableForPaymentScreen.includes(row.status)) {
                                                  *             navigate(`/level-3-payment-details`, {
                                                  *                 state: { id: row.id },
                                                  *             });
                                                  *         } else if (levelThreeActionable.includes(row.status)) {
                                                  *             navigate(`/level-three-application-detail`, {
                                                  *                 state: { id: row.id },
                                                  *             });
                                                  *         } else {
                                                  *             navigate(`/view-application-details`, {
                                                  *                 state: { id: row.id },
                                                  *             });
                                                  *         }
                                                  *     }}
                                                  * />
                                                  */}
                                                <ViewButton onClick={() => openApplication(row)} />

                                                <OverrideButton
                                                    onClick={() => handleOverrideClick(row)}
                                                    disabled={!overridableStatuses.includes(row?.status)}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 flex">
                                            <p className="font-bold text-base w-1/2">
                                                Application ID
                                            </p>
                                            <p className="text-base mx-2">:</p>
                                            <p className="text-base w-2/3">{row.applicationId}</p>
                                        </div>
                                        <div className="mb-2 flex">
                                            <p className="font-bold text-base w-1/2">
                                                Plot Owner's Name
                                            </p>
                                            <p className="text-base mx-2">:</p>
                                            <p className="text-base w-2/3">
                                                {row.plot_owner_s_name || "-"}
                                            </p>
                                        </div>
                                        <div className="mb-2 flex">
                                            <p className="font-bold text-base w-1/2">
                                                Date of Submission
                                            </p>
                                            <p className="text-base mx-2">:</p>
                                            <p className="text-base w-2/3">
                                                {formatDate(row.submittedAt || "x")}
                                            </p>
                                        </div>
                                        <div className="mb-2 flex">
                                            <p className="font-bold text-base w-1/2">
                                                Days Left for Action
                                            </p>
                                            <p className="text-base mx-2">:</p>
                                            {row.daysLeftForAction === null ||
                                                row.daysLeftForAction === undefined ? (
                                                <p className="text-base w-2/3">-</p>
                                            ) : row.daysLeftForAction === 0 ? (
                                                <p className="text-base w-2/3 text-red-600 font-bold">
                                                    0
                                                </p>
                                            ) : (
                                                <p
                                                    className={`text-base w-2/3 ${row.daysLeftForAction <= 2
                                                        ? "text-red-600 font-bold"
                                                        : ""
                                                        }`}
                                                >
                                                    {row.daysLeftForAction}
                                                </p>
                                            )}
                                        </div>
                                        {/* </div> */}
                                        {/* </div> */}
                                    </div>
                                );
                            })}
                        </div>
                        <Paginator
                            limit={10}
                            page={page}
                            setPage={setPage}
                            count={counts?.data || 0}
                        />
                    </BoxComponent>
                </>
            ) : (
                <BoxComponent>
                    <DataTable
                        pagination
                        sortServer
                        paginationServer
                        className="mt-4 rounded-0"
                        columns={columns}
                        //data={data?.data || []}
                        data={sortedData}
                        sortIcon={<SortIcon />}
                        customStyles={tableCustomStyles}
                        onChangePage={(p) => setPage(p)}
                        paginationTotalRows={counts?.data || 0}
                        progressPending={isLoading1 || isLoading2}
                        onSort={(col, dir) => {
                            console.log("🔃 onSort fired", col?.sortField, dir);
                            if (!col?.sortField) return;
                            setPage(1);
                            setSort({
                                sortKey: col.sortField || "",
                                sortOrder: dir === "asc" ? 1 : -1,
                            });
                        }}
                        // paginationComponentOptions={{
                        //     noRowsPerPage: true,
                        //     selectAllRowsItem: false,
                        // }}
                        paginationComponent={PaginatorForRDT}
                        // defaultSortFieldId="submittedAt"
                        // defaultSortAsc={false}
                    />
                </BoxComponent>
            )}

            {/* Bootstrap Modal */}
            <OverrideDialog
                show={showModal}
                onHide={handleModalClose}
                onConfirm={handleOverrideConfirm}
                overrideReason={overrideReason}
                setOverrideReason={setOverrideReason}
                textAreaRef={textAreaRef}
                overrideType={selectedApplication ? determineOverrideType(selectedApplication.status) : "application"}
                overrideStatus={selectedApplication?.status}
            />

            {/* Filter Dialog */}
            <Dialog
                onClose={toggleFilter}
                open={filterOpen}
                className="flex flex-col sm:w-[600px] lg:w-[800px]"
            >
                <ApplicationsFilter
                    onClose={toggleFilter}
                    defaultFilter={filterState}
                    onChange={setFilterState}
                />
            </Dialog>
        </div>
    );
};

export default LevelThreeApplications;
